import isPropValid from '@emotion/is-prop-valid';
import { CSSObject } from '@emotion/react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { ElementType, forwardRef, HTMLProps, PropsWithChildren, Ref } from 'react';
import { isIE } from 'react-device-detect';

import { getSpace } from './helpers';
import styles, { BorderRadius } from '../../constants/styles';
import { FontColor, FontSize, NumericFontSize, Theme } from '../../theme';
import { ColorKey, getColorVariable, getSemanticColorVariable, SemanticColorKey } from '../../theme/colors';

// Local Typings
export type BorderRadiusNumber = 0 | 2 | 4 | 8;
export type BorderWidth = 0 | 1 | 2;

type BorderColor =
  | 'transparent'
  | SemanticColorKey;
type FontWeight = 'bold' | 'inherit' | 'normal' | 'semiBold';
type FontWeightNumber = 400 | 600 | 700;

export interface MarginProps {
  margin?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginX?: number;
  marginY?: number;
}

interface BoxUIProps extends MarginProps {
  alignContent?: CSSObject['alignContent'];
  alignItems?: CSSObject['alignItems'];
  alignSelf?: CSSObject['alignSelf'];
  alignment?: 'center' | 'left' | 'right';
  ariaLabel?: string;
  as?: ElementType;
  backgroundColor?: ColorKey | SemanticColorKey; // TODO: remove ColorKey
  border?: CSSObject['border'];
  borderBottom?: CSSObject['borderBottom'];
  borderBottomLeftRadius?: BorderRadiusNumber;
  borderBottomRightRadius?: BorderRadiusNumber;
  borderColor?: BorderColor;
  borderLeft?: CSSObject['borderLeft'];
  borderRadius?: BorderRadius | number;
  borderRight?: CSSObject['borderRight'];
  borderStyle?: CSSObject['borderStyle'];
  borderTop?: CSSObject['borderTop'];
  borderTopLeftRadius?: BorderRadiusNumber;
  borderTopRightRadius?: BorderRadiusNumber;
  borderWidth?: BorderWidth;
  bottom?: CSSObject['bottom'];
  cursor?: CSSObject['cursor'];
  display?: CSSObject['display'];
  flex?: CSSObject['flex'];
  flexBasis?: CSSObject['flexBasis'];
  flexDirection?: CSSObject['flexDirection'];
  flexGrow?: CSSObject['flexGrow'];
  flexShrink?: CSSObject['flexShrink'];
  flexWrap?: CSSObject['flexWrap'];
  fontColor?: 'inherit' | FontColor;
  fontFamily?: string;
  fontSize?: 'inherit' | FontSize | NumericFontSize;
  fontWeight?: FontWeight;
  fullHeight?: boolean;
  fullStoryMask?: boolean;
  fullWidth?: boolean;
  gap?: number;
  gapX?: number;
  gapY?: number;
  gridArea?: string;
  gridTemplateAreas?: string;
  gridTemplateColumns?: string;
  gridTemplateRows?: string;
  height?: CSSObject['height'];
  hideScrollBar?: boolean;
  italic?: boolean;
  justifyContent?: CSSObject['justifyContent'];
  left?: CSSObject['left'];
  letterSpacing?: CSSObject['letterSpacing'];
  lineHeight?: string;
  maxHeight?: CSSObject['maxHeight'];
  maxWidth?: CSSObject['maxWidth'];
  minHeight?: CSSObject['minHeight'];
  minWidth?: CSSObject['minWidth'];
  noWrap?: boolean;
  opacity?: CSSObject['opacity'];
  outline?: CSSObject['outline'];
  overflow?: CSSObject['overflow'];
  overflowX?: CSSObject['overflowX'];
  overflowY?: CSSObject['overflowY'];
  padding?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingX?: number;
  paddingY?: number;
  pointerEvents?: CSSObject['pointerEvents'];
  position?: CSSObject['position'];
  ref?: Ref<HTMLElement>;
  right?: CSSObject['right'];
  testId?: string;
  textDecoration?: CSSObject['textDecoration'];
  top?: CSSObject['top'];
  touchAction?: CSSObject['touchAction'];
  transform?: CSSObject['transform'];
  transition?: CSSObject['transition'];
  truncateWidth?: number | string;
  userSelect?: CSSObject['userSelect'];
  visibility?: CSSObject['visibility'];
  width?: CSSObject['width'];
  withBoxShadow?: boolean;
  wordBreak?: boolean;
  zIndex?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type BoxProps = PropsWithChildren<BoxUIProps & HTMLProps<any>>;

export const defaultBorderWidth = 1;

const getBorderRadius = (borderRadius: BoxProps['borderRadius']) => {
  if (typeof borderRadius === 'number') {
    return borderRadius;
  }

  return borderRadius ? styles.borderRadius[borderRadius] : undefined;
};

const getSpaceProps = ({
  margin,
  marginBottom,
  marginLeft,
  marginRight,
  marginTop,
  marginX,
  marginY,
  padding,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingX,
  paddingY,
}: BoxUIProps,
theme: Theme) => {
  const marginProps = [
    margin,
    marginBottom,
    marginLeft,
    marginRight,
    marginTop,
    marginX,
    marginY,
  ];
  const paddingProps = [
    padding,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingX,
    paddingY,
  ];

  const style: CSSObject = {};

  if (marginProps.some(value => typeof value !== 'undefined')) {
    Object.assign(style,
      {
        marginBottom: getSpace([marginBottom, marginY, margin],
          theme.spacing),
        marginLeft: getSpace([marginLeft, marginX, margin],
          theme.spacing),
        marginRight: getSpace([marginRight, marginX, margin],
          theme.spacing),
        marginTop: getSpace([marginTop, marginY, margin],
          theme.spacing),
      });
  }

  if (paddingProps.some(value => typeof value !== 'undefined')) {
    Object.assign(style,
      {
        paddingBottom: getSpace([paddingBottom, paddingY, padding],
          theme.spacing),
        paddingLeft: getSpace([paddingLeft, paddingX, padding],
          theme.spacing),
        paddingRight: getSpace([paddingRight, paddingX, padding],
          theme.spacing),
        paddingTop: getSpace([paddingTop, paddingY, padding],
          theme.spacing),
      });
  }

  return style;
};

const fontWeights: Record<FontWeight, 'inherit' | FontWeightNumber> = {
  bold: 700,
  inherit: 'inherit',
  normal: 400,
  semiBold: 600,
};

const getFontWeight = (fontWeight: BoxUIProps['fontWeight']) => {
  if (!fontWeight) {
    return undefined;
  }

  if (isIE && (fontWeight !== 'normal')) {
    return 'bold';
  }

  return fontWeights[fontWeight];
};

const getFontSize = (fontSize: BoxUIProps['fontSize'],
  theme: Theme) => {
  if (!fontSize) {
    return undefined;
  }

  if (fontSize === 'inherit') {
    return 'inherit';
  }

  if (typeof fontSize === 'number') {
    return `${fontSize}px`;
  }

  return theme.sizes.fontSizes[fontSize];
};

const getFontColor = (color: 'inherit' | FontColor | undefined) => {
  if (color === 'inherit') {
    return 'inherit';
  }

  return getColorVariable(color as ColorKey); // TODO: remove ColorKey
};

const invalidProps = [
  'alignContent',
  'alignItems',
  'alignment',
  'alignSelf',
  'background',
  'backgroundColor',
  'border',
  'borderBottom',
  'borderColor',
  'borderLeft',
  'borderRadius',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderRight',
  'borderStyle',
  'borderTop',
  'borderWidth',
  'cursor',
  'display',
  'flex',
  'flexBasis',
  'flexDirection',
  'flexGrow',
  'flexShrink',
  'flexWrap',
  'fontColor',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fullHeight',
  'fullStoryMask',
  'fullWidth',
  'gap',
  'gapX',
  'gapY',
  'gridArea',
  'gridTemplateAreas',
  'gridTemplateColumns',
  'gridTemplateRows',
  'height',
  'hideScrollBar',
  'italic',
  'justifyContent',
  'lineHeight',
  'margin',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'marginTop',
  'marginX',
  'marginY',
  'maxHeight',
  'maxWidth',
  'minHeight',
  'minWidth',
  'noWrap',
  'opacity',
  'outline',
  'overflow',
  'overflowX',
  'overflowY',
  'padding',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'paddingX',
  'paddingY',
  'pointerEvents',
  'position',
  'textDecoration',
  'top',
  'transform',
  'transition',
  'bottom',
  'right',
  'left',
  'truncateWidth',
  'visibility',
  'width',
  'withBoxShadow',
  'wordBreak',
  'zIndex',
];

const StyledDiv = styled('div',
  {
    shouldForwardProp: prop => isPropValid(prop) && !invalidProps.includes(prop),
  })<BoxUIProps>(props => {
  const {
    alignContent,
    alignItems,
    alignSelf,
    alignment,
    ariaLabel,
    backgroundColor,
    border,
    borderBottom,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderColor,
    borderLeft,
    borderRadius,
    borderRight,
    borderStyle = 'solid',
    borderTop,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderWidth = defaultBorderWidth,
    bottom,
    cursor,
    display,
    flex,
    flexBasis,
    flexDirection,
    flexGrow,
    flexShrink,
    flexWrap,
    fontColor,
    fontFamily,
    fontSize,
    fontWeight,
    fullHeight,
    fullWidth,
    gap,
    gapX,
    gapY,
    gridArea,
    gridTemplateAreas,
    gridTemplateColumns,
    gridTemplateRows,
    height,
    hideScrollBar,
    italic,
    justifyContent,
    left,
    letterSpacing,
    lineHeight,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    noWrap,
    opacity,
    outline,
    overflow,
    overflowX,
    overflowY,
    pointerEvents,
    position,
    right,
    textDecoration,
    theme,
    top,
    touchAction,
    transform,
    transition,
    truncateWidth,
    userSelect,
    visibility,
    width,
    withBoxShadow,
    wordBreak,
    zIndex,
  } = props;

  const computedBorderColor = getSemanticColorVariable(borderColor);

  const spacingCss = getSpaceProps(props,
    theme);

  return {
    alignContent,
    alignItems,
    alignSelf,
    ariaLabel,
    /* eslint-disable sort-keys */
    backgroundColor: getColorVariable(backgroundColor as ColorKey), // TODO: remove ColorKey
    border,

    // border radius order matters to target specific corners
    borderRadius: getBorderRadius(borderRadius),
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderTopLeftRadius,
    borderTopRightRadius,

    borderStyle: computedBorderColor ? borderStyle : undefined,
    borderWidth: computedBorderColor ? borderWidth : undefined,

    // these need to be set after borderWidth
    borderTop,
    borderLeft,
    borderRight,
    borderBottom,

    // these need to be set after border
    borderColor: computedBorderColor,

    bottom,
    boxShadow: withBoxShadow ? '0px 0px 12px 0px rgba(0, 34, 56, 0.16)' : undefined,
    /* eslint-enable sort-keys */
    color: getFontColor(fontColor),
    columnGap: gapX ? gapX * theme.spacing : undefined,
    cursor,
    display,
    flex,
    flexBasis,
    flexDirection,
    flexGrow,
    flexShrink,
    flexWrap,
    fontFamily,
    fontSize: getFontSize(fontSize,
      theme),
    fontStyle: italic ? 'italic' : undefined,
    fontWeight: getFontWeight(fontWeight),
    gap: gap ? gap * theme.spacing : undefined,
    gridArea,
    gridTemplateAreas,
    gridTemplateColumns,
    gridTemplateRows,
    height: fullHeight ? '100%' : height,
    hyphens: wordBreak ? 'auto' : undefined,
    justifyContent,
    left,
    letterSpacing,
    lineHeight,
    maxHeight,
    maxWidth: maxWidth ?? truncateWidth,
    minHeight,
    minWidth,
    opacity,
    outline,
    outlineColor: getColorVariable('v3_navy_900'),
    overflow: truncateWidth ? 'hidden' : overflow,
    overflowX,
    overflowY,
    pointerEvents,
    position,
    right,
    rowGap: gapY ? gapY * theme.spacing : undefined,
    textAlign: alignment,
    textDecoration,
    textOverflow: truncateWidth ? 'ellipsis' : undefined,
    top,
    touchAction,
    transform,
    transition,
    userSelect,
    visibility,
    whiteSpace: noWrap || truncateWidth ? 'nowrap' : undefined,
    width: fullWidth ? '100%' : width,
    wordBreak: wordBreak ? 'break-word' : undefined,
    zIndex,
    ...spacingCss,
    ...(hideScrollBar
      ? {
        msOverflowStyle: 'none', /* IE and Edge */
        scrollbarWidth: 'none', /* Firefox */

        // eslint-disable-next-line sort-keys,@typescript-eslint/naming-convention
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }
      : {}),
  };
});

// Component Definition
const Box = forwardRef<HTMLElement, BoxProps>(({
  as = 'div',
  children,
  ...props
},
ref) => {
  const className = props.fullStoryMask
    ? classNames(props.className, 'fs-exclude')
    : props.className;

  return (
    <StyledDiv
      as={as}
      {...props}
      className={className}
      data-testid={props.testId}
      ref={ref as Ref<HTMLDivElement>}
    >
      {children}
    </StyledDiv>
  );
});

export default Box;
